import React, { useState } from 'react';
import { formatDistanceToNow, format, isValid, parseISO } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { supabase, supabaseConfigured } from '../supabaseClient';

function formatLastMessageTime(timeString) {
  if (!timeString) return { relative: 'N/A', utc: 'N/A' };
  try {
    const date = parseISO(timeString);
    if (!isValid(date)) return { relative: 'Invalid Date', utc: 'Invalid Date' };
    return {
      relative: formatDistanceToNow(date, { addSuffix: true, locale: enUS }),
      utc: format(date, "yyyy-MM-dd HH:mm:ss 'UTC'"),
    };
  } catch {
    return { relative: 'Invalid Date', utc: 'Invalid Date' };
  }
}

// Logo glyph: cyan circle, central filled disc, four "stars" — matches the
// Visual Reference symbol exactly.
const LogoMark = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1" fill="none" />
    <circle cx="12" cy="12" r="3" fill="currentColor" />
    <circle cx="6" cy="6" r="0.7" fill="currentColor" />
    <circle cx="18" cy="7" r="0.7" fill="currentColor" />
    <circle cx="7" cy="18" r="0.7" fill="currentColor" />
    <circle cx="18" cy="17" r="0.7" fill="currentColor" />
  </svg>
);

const Header = React.memo(({
  stats, lastMessageTime, staleCount = 0, session, view, onViewChange,
  isAdmin, onOpenAdmin, onOpenData, onOpenRegister, onOpenObserve,
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');

  const formattedTime = formatLastMessageTime(lastMessageTime);
  const messageClass = message.startsWith('Error')
    ? 'auth-message auth-message--error'
    : 'auth-message auth-message--success';

  const handleLogin = async (event) => {
    event.preventDefault();
    if (!supabase) return;
    setMessage('');
    setLoading(true);
    const { error } = await supabase.auth.signInWithOtp({ email });
    setLoading(false);
    if (error) setMessage(`Error: ${error.message}`);
    else setMessage('Check your email for the login link!');
  };

  const handleLogout = async () => {
    if (!supabase) return;
    setMessage('');
    const { error } = await supabase.auth.signOut();
    if (error) setMessage(`Logout Error: ${error.message}`);
  };

  // Network-stat row — IBM Plex Mono with a cyan ● and · separators.
  const StatsDisplay = () => (
    <div className="header-stats" title={`Last data received: ${formattedTime.utc}`}>
      <span><span className="stat-dot">●</span> {stats.stationCount} stations</span>
      {staleCount > 0 && (
        <>
          <span className="sep">·</span>
          <span
            className="header-stat-warn"
            title={`${staleCount} station${staleCount === 1 ? ' has' : 's have'} not reported for over 24 hours`}
          >
            <span className="stat-dot stat-dot--warn">●</span> {staleCount} not reporting
          </span>
        </>
      )}
      <span className="sep">·</span>
      <span>
        {stats.averageRaw != null ? `${Math.round(stats.averageRaw)} avg raw` : 'no readings'}
      </span>
      <span className="sep">·</span>
      <span>last update {formattedTime.relative}</span>
    </div>
  );

  const AuthSection = ({ vertical }) => {
    if (!supabaseConfigured) return null;
    if (!session) {
      return (
        <form onSubmit={handleLogin} style={{ display: 'flex', gap: 8, flexDirection: vertical ? 'column' : 'row', alignItems: vertical ? 'stretch' : 'center' }}>
          <input
            type="email"
            placeholder="your@email.com"
            value={email}
            required
            onChange={(e) => setEmail(e.target.value)}
            className="input-field"
            aria-label="Email address"
            style={{ width: vertical ? '100%' : 200, fontSize: 12 }}
          />
          <button className="btn" type="submit" disabled={loading}>
            {loading ? 'Sending…' : 'Magic link'}
          </button>
          {message && <span className={messageClass}>{message}</span>}
        </form>
      );
    }
    return (
      <>
        <span style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'IBM Plex Mono, monospace' }}>
          {session.user.email}
        </span>
        {isAdmin && <button className="ods-btn warn" onClick={onOpenAdmin}>Admin</button>}
        <button className="btn" onClick={handleLogout}>Logout</button>
      </>
    );
  };

  return (
    <header className="header">
      <div className="header-left">
        <a
          href="https://opendarksky.org"
          target="_blank"
          rel="noopener noreferrer"
          className="header-logo"
          aria-label="Open Dark Sky — open project page"
        >
          <LogoMark className="header-mark" aria-hidden="true" />
          <span className="header-wordmark">Open Dark Sky</span>
        </a>
        <button
          className="about-toggle"
          onClick={() => setAboutOpen((v) => !v)}
          aria-label="About this map"
          aria-expanded={aboutOpen}
          title="About this map"
        >?</button>
        {aboutOpen && (
          <div className="about-popover" role="dialog" aria-label="About this map">
            <h4>The dark-sky commons</h4>
            <p>
              Open Dark Sky is a citizen-science project mapping night-sky brightness.
              Anyone can build a sensor or report what they see overhead. All data CC-BY-4.0.
            </p>
            <h4>What is "mag/arcsec²"?</h4>
            <p>
              The standard astronomy unit for sky brightness — higher values mean a darker
              sky. ~22 is pristine wilderness; ~18 is suburban; &lt;17 is bright urban.
            </p>
            <h4>Tips</h4>
            <ul>
              <li>Click any station to open its drawer.</li>
              <li>Hold <kbd>Shift</kbd> + drag on the map to compare a region.</li>
              <li>Use the time scrubber to look at a past moment.</li>
            </ul>
            <button className="btn-table" onClick={() => setAboutOpen(false)}>Close</button>
          </div>
        )}

        <div className="seg-ctl desktop-nav" role="tablist" aria-label="View">
          <button role="tab" aria-pressed={view === 'map'} className={view === 'map' ? 'on' : ''} onClick={() => onViewChange('map')}>Map</button>
          <button role="tab" aria-pressed={view === 'table'} className={view === 'table' ? 'on' : ''} onClick={() => onViewChange('table')}>Table</button>
        </div>
      </div>

      <div className="header-spacer" />

      <div className="desktop-nav" style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        <StatsDisplay />
      </div>

      <div className="desktop-nav" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <button className="btn" onClick={() => onOpenObserve?.()}>🌙 Report sky</button>
        <button className="btn" onClick={() => onOpenRegister?.()}>Register station</button>
        <button className="btn" onClick={() => onOpenData?.()}>Data &amp; API</button>
        <AuthSection vertical={false} />
      </div>

      <button
        className="hamburger"
        onClick={() => setIsMenuOpen(!isMenuOpen)}
        aria-label="Toggle navigation menu"
        aria-expanded={isMenuOpen}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M4 6H20M4 12H20M4 18H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {isMenuOpen && (
        <div className="mobile-menu" role="navigation" aria-label="Mobile navigation">
          <div className="seg-ctl">
            <button className={view === 'map' ? 'on' : ''} onClick={() => { onViewChange('map'); setIsMenuOpen(false); }}>Map</button>
            <button className={view === 'table' ? 'on' : ''} onClick={() => { onViewChange('table'); setIsMenuOpen(false); }}>Table</button>
          </div>
          <button className="btn" onClick={() => { onOpenObserve?.(); setIsMenuOpen(false); }}>🌙 Report sky</button>
          <button className="btn" onClick={() => { onOpenRegister?.(); setIsMenuOpen(false); }}>Register station</button>
          <button className="btn" onClick={() => { onOpenData?.(); setIsMenuOpen(false); }}>Data &amp; API</button>
          <AuthSection vertical={true} />
          <StatsDisplay />
        </div>
      )}
    </header>
  );
});

export default Header;
